import { router } from 'expo-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, Modal, Pressable, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import type { PolfinancePolicy } from '@/api/mobile-api';

import { PolicyCard } from '@/components/policy-card';
import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { PrimaryButton } from '@/components/ui/primary-button';
import { ProgressRing } from '@/components/ui/progress-ring';
import { useAuthStore } from '@/store/auth-store';
import { useHouseholdStore } from '@/store/household-store';
import { usePoliciesStore } from '@/store/policies-store';
import { colors, radius, spacing } from '@/theme/tokens';
import { daysUntil, formatDate, formatPln } from '@/utils/format';
import { computeProtection } from '@/utils/protection';

const PREMIUM_PERIOD_PL: Record<string, string> = {
  ANNUAL: 'rocznie',
  SEMI_ANNUAL: 'półrocznie',
  QUARTERLY: 'kwartalnie',
  MONTHLY: 'miesięcznie',
};

/** Policy wallet — mock: portfel_polis_nowa_struktura/screen.png */
export default function PoliciesScreen() {
  const { t } = useTranslation();
  const policies = usePoliciesStore((s) => s.policies);
  const polfinancePolicies = usePoliciesStore((s) => s.polfinancePolicies);
  const loadPolfinancePolicies = usePoliciesStore((s) => s.loadPolfinancePolicies);
  const household = useHouseholdStore((s) => s.household);
  const account = useAuthStore((s) => s.account);
  const matchStatus = useAuthStore((s) => s.matchStatus);

  const linked = account?.linkStatus === 'LINKED';
  useEffect(() => {
    if (linked) {
      loadPolfinancePolicies();
    }
  }, [linked, loadPolfinancePolicies]);

  // Preview a Polfinance policy's document (the scan the agent attached in the
  // CRM) — this is the app's core promise (owner). Image -> in-app modal;
  // PDF/other -> system browser via the presigned URL.
  const [viewUri, setViewUri] = useState<string | null>(null);
  const openPolfinanceDoc = async (policy: PolfinancePolicy) => {
    if (!policy.documentUrl) return;
    const isImage =
      (policy.documentContentType ?? '').startsWith('image') ||
      /\.(jpe?g|png|heic|webp|gif)(\?|$)/i.test(policy.documentUrl);
    if (isImage) {
      setViewUri(policy.documentUrl);
      return;
    }
    try {
      const WebBrowser = await import('expo-web-browser');
      await WebBrowser.openBrowserAsync(policy.documentUrl);
    } catch {
      const { Linking } = await import('react-native');
      await Linking.openURL(policy.documentUrl).catch(() => undefined);
    }
  };

  const protection = household ? computeProtection(household.declarations, policies) : null;
  const canOfferLink = !linked && (matchStatus === 'SINGLE_MASKED' || matchStatus === 'MULTIPLE');

  // Split finished policies out of the main list (owner: "niektóre już się dawno
  // skończyły, a wiszą razem z aktywnymi"). Open-ended never counts as ended.
  const todayIso = new Date().toISOString().slice(0, 10);
  const isEnded = (p: (typeof policies)[number]) => !p.openEnded && !!p.endDate && p.endDate < todayIso;
  const activePolicies = policies.filter((p) => !isEnded(p));
  const endedPolicies = policies.filter(isEnded);

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top']}>
      <ScrollView contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}>
        <AppText variant="headlineSm" color={colors.navy}>
          {t('policies.title')}
        </AppText>

        {/* Protection level — ring left, text right (new mock layout) */}
        {protection && (
          <Card style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.md }}>
            <ProgressRing percent={protection.percent} size={96} strokeWidth={9}>
              <View style={{ alignItems: 'center' }}>
                <AppText variant="headlineSm" color={colors.navy}>
                  {protection.percent}%
                </AppText>
                <AppText variant="labelMd">{t('policies.protectionRingLabel')}</AppText>
              </View>
            </ProgressRing>
            <View style={{ flex: 1, gap: spacing.xs }}>
              <AppText variant="headlineSm">{t('policies.protectionTitle')}</AppText>
              <AppText variant="bodyMd">
                {t('policies.protectionBody', { count: policies.length })}
              </AppText>
              <PrimaryButton
                label={t('policies.completeProfile')}
                onPress={() => router.push('/protection')}
                style={{ minHeight: 40, paddingVertical: spacing.xs, alignSelf: 'flex-start' }}
              />
            </View>
          </Card>
        )}

        {/* Linked account: Polfinance policies straight from the CRM (read-only). */}
        {linked && polfinancePolicies.length > 0 && (
          <View style={{ gap: spacing.md }}>
            <AppText variant="headlineSm">{t('policies.polfinanceHeader')}</AppText>
            {polfinancePolicies.map((policy) => {
              // Same "variant A" language as local cards: status pill (dot),
              // calendar + od–do dates, minimized icons. Green doc icon stays.
              const ended =
                policy.active === false ||
                (!!policy.endDate && policy.endDate < todayIso);
              const st = ended
                ? { fg: colors.outline, bg: colors.surface, label: t('policies.statusEnded') }
                : { fg: colors.success, bg: colors.successBg, label: t('policies.statusActive') };
              const dateRange = policy.endDate
                ? `${policy.startDate ? formatDate(policy.startDate) : '—'}  →  ${formatDate(policy.endDate)}`
                : policy.startDate
                  ? t('policies.coverageFrom', { date: formatDate(policy.startDate) })
                  : null;
              // Time-progress bar (as in the mock) — needs both dates, not ended.
              let elapsedPct: number | null = null;
              if (policy.startDate && policy.endDate && !ended) {
                const total = daysUntil(policy.endDate) - daysUntil(policy.startDate);
                const left = daysUntil(policy.endDate);
                if (total > 0) elapsedPct = Math.max(0, Math.min(100, ((total - left) / total) * 100));
              }
              return (
                <Card key={policy.id} style={{ gap: spacing.sm }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
                    <Ionicons name="shield-checkmark" size={22} color={colors.primary} />
                    <View style={{ flex: 1 }}>
                      <AppText variant="headlineSm">
                        {policy.productName ?? policy.insurerName ?? t('policies.polfinancePolicy')}
                      </AppText>
                      <AppText variant="bodyMd">
                        {[policy.insurerName, policy.policyNumber].filter(Boolean).join(' · ')}
                      </AppText>
                    </View>
                    {/* Green document icon ONLY when a scan/PDF exists in the CRM
                        (owner: nothing when there's no document). */}
                    {policy.documentUrl && (
                      <Pressable
                        accessibilityRole="button"
                        accessibilityLabel={t('policies.viewDocument')}
                        onPress={() => openPolfinanceDoc(policy)}
                        hitSlop={8}
                        style={{ minWidth: 32, minHeight: 32, alignItems: 'center', justifyContent: 'center' }}
                      >
                        <Ionicons name="document-text" size={22} color={colors.success} />
                      </Pressable>
                    )}
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 6,
                        backgroundColor: st.bg,
                        borderRadius: radius.full,
                        paddingVertical: 4,
                        paddingHorizontal: spacing.sm,
                      }}
                    >
                      <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: st.fg }} />
                      <AppText variant="labelMd" color={st.fg}>
                        {st.label}
                      </AppText>
                    </View>
                  </View>

                  {dateRange && (
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs }}>
                      <Ionicons name="calendar-outline" size={16} color={colors.outline} />
                      <AppText variant="labelLg" style={{ fontSize: 15 }}>
                        {dateRange}
                      </AppText>
                    </View>
                  )}

                  {elapsedPct !== null && policy.endDate && (
                    <View style={{ gap: 6 }}>
                      <View style={{ height: 6, borderRadius: radius.full, backgroundColor: colors.surface, overflow: 'hidden' }}>
                        <View
                          style={{
                            height: 6,
                            width: `${elapsedPct}%`,
                            borderRadius: radius.full,
                            backgroundColor: daysUntil(policy.endDate) < 30 ? colors.warning : colors.success,
                          }}
                        />
                      </View>
                      <AppText variant="labelMd" color={daysUntil(policy.endDate) < 30 ? colors.warning : colors.textSecondary}>
                        {t('common.daysLeft', { count: daysUntil(policy.endDate) })}
                      </AppText>
                    </View>
                  )}

                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <AppText variant="labelLg" style={{ flex: 1, fontSize: 15 }}>
                      {policy.premiumGr != null
                        ? `${formatPln(policy.premiumGr)} ${PREMIUM_PERIOD_PL[policy.premiumPeriod ?? ''] ?? ''}`.trim()
                        : ''}
                    </AppText>
                    <View
                      style={{
                        backgroundColor: colors.infoBg,
                        borderRadius: radius.full,
                        paddingHorizontal: spacing.sm,
                        paddingVertical: 2,
                      }}
                    >
                      <AppText variant="labelMd" color={colors.primary}>
                        Polfinance
                      </AppText>
                    </View>
                  </View>
                </Card>
              );
            })}
          </View>
        )}

        {/* Logged-in with a phone match but not linked yet: one-tap offer. */}
        {canOfferLink && (
          <Pressable
            accessibilityRole="button"
            onPress={() => router.push('/auth/connect')}
            style={({ pressed }) => ({ transform: [{ scale: pressed ? 0.98 : 1 }] })}
          >
            <Card style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, backgroundColor: colors.infoBg }}>
              <Ionicons name="link-outline" size={28} color={colors.primary} />
              <View style={{ flex: 1 }}>
                <AppText variant="labelLg" color={colors.navy}>
                  {t('auth.connectTitle')}
                </AppText>
                <AppText variant="bodyMd">{t('auth.connectSubtitle')}</AppText>
              </View>
              <Ionicons name="chevron-forward" size={18} color={colors.outlineVariant} />
            </Card>
          </Pressable>
        )}

        {/* Human fallback in progress: keep the user informed, no dead ends. */}
        {account?.linkStatus === 'HELP_REQUESTED' && (
          <Card style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, backgroundColor: colors.infoBg }}>
            <Ionicons name="hourglass-outline" size={24} color={colors.primary} />
            <AppText variant="bodyMd" style={{ flex: 1 }}>
              {t('auth.helpPendingInfo')}
            </AppText>
          </Card>
        )}

        <View style={{ gap: spacing.base }}>
          <AppText variant="headlineLg">{t('policies.header')}</AppText>
          <AppText variant="bodyMd">{t('policies.subtitle', { count: activePolicies.length })}</AppText>
        </View>

        <View style={{ gap: spacing.md }}>
          {activePolicies.map((policy) => (
            <PolicyCard
              key={policy.id}
              policy={policy}
              onDetails={() => router.push({ pathname: '/policy/[id]', params: { id: policy.id } })}
            />
          ))}
        </View>

        {/* Finished policies — grouped separately and dimmed so they don't clutter
            the active wallet, but stay reachable (renewals, history). */}
        {endedPolicies.length > 0 && (
          <>
            <AppText variant="headlineSm" color={colors.outline}>
              {t('policies.endedHeader', { count: endedPolicies.length })}
            </AppText>
            <View style={{ gap: spacing.md, opacity: 0.65 }}>
              {endedPolicies.map((policy) => (
                <PolicyCard
                  key={policy.id}
                  policy={policy}
                  onDetails={() => router.push({ pathname: '/policy/[id]', params: { id: policy.id } })}
                />
              ))}
            </View>
          </>
        )}

        {/* Reciprocity: adding works before any account exists */}
        <View
          style={{
            borderWidth: 2,
            borderStyle: 'dashed',
            borderColor: colors.outlineVariant,
            borderRadius: radius.card,
            padding: spacing.lg,
            alignItems: 'center',
            gap: spacing.md,
          }}
        >
          <Ionicons name="shield-outline" size={40} color={colors.outline} />
          <AppText variant="bodyMd" style={{ textAlign: 'center' }}>
            {t('policies.missingTitle')}{'\n'}
            {t('policies.missingSubtitle')}
          </AppText>
          <PrimaryButton
            label={t('policies.addScan')}
            onPress={() => router.push({ pathname: '/policy/new', params: { mode: 'scan' } })}
            style={{ alignSelf: 'stretch' }}
          />
          <PrimaryButton
            label={t('policies.addManual')}
            variant="secondary"
            onPress={() => router.push('/policy/new')}
            style={{ alignSelf: 'stretch' }}
          />
        </View>
      </ScrollView>

      {/* Full-screen image preview for a Polfinance policy document */}
      <Modal visible={viewUri !== null} transparent animationType="fade" onRequestClose={() => setViewUri(null)}>
        <Pressable
          onPress={() => setViewUri(null)}
          style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.9)', padding: spacing.md, justifyContent: 'center' }}
        >
          {viewUri && <Image source={{ uri: viewUri }} style={{ flex: 1, borderRadius: radius.card }} resizeMode="contain" />}
        </Pressable>
      </Modal>
    </SafeAreaView>
  );
}
